<!-- Analysis Table — filter toolbar and selection handled entirely by DataTableComponent -->
<app-data-table
  [data]="tableData()"
  [columns]="columns()"
  [isLoading]="isLoading()"
  [currentPage]="currentPage()"
  [totalPages]="totalPages()"
  [enableRowSelection]="selectable() && currentOrgStore.can('analysis', 'update')"
  [rowId]="getRowId"
  [totalItems]="totalItems()"
  [isSelectingAll]="isSelectingAll()"
  [rowActions]="actions()"
  [selectionActions]="selectionActions()"
  [toolbarActions]="currentOrgStore.can('campaign', 'update') ? toolbarActions() : []"
  [rowClass]="getRowClass.bind(this)"
  [showAddButton]="showAddButton() && currentOrgStore.can('organization', 'createanalysis')"
  addNewLabel="Neue Analyse"
  searchPlaceholder="Adresse oder ID suchen"
  emptyStateTitle="Keine Analysen gefunden"
  emptyStateDescription="Es wurden noch keine Analysen erstellt."
  (addNew)="onAddNew()"
  (rowClick)="onRowClick($event)"
  (selectionIdsChanged)="onSelectionIdsChanged($event)"
  (requestSelectAll)="requestSelectAll.emit()"
  (pageChange)="pageChange.emit($event)"
></app-data-table>

<!-- Create Analysis Dialog -->
@if (currentOrgStore.can('organization', 'createanalysis')) {
  <app-create-analysis-dialog
    #createDialog
    [organizationId]="organizationId()"
    [campaignId]="campaignId()"
    (submitted)="onAnalysisCreated($event)"
  />
}

<!-- ═══ Column templates ════════════════════════════════════════════════════ -->

<ng-template #analysisNameTpl let-analysis>
  <div class="min-w-56">
    <app-analysis-reference class="max-w-80" [analysisId]="analysis.analysisId" [address]="analysis.address" [organizationSlug]="currentOrgStore.organizationSlug()!" />
    @if (isArchived(analysis)) { <span hlmBadge variant="secondary" class="h-6 rounded-md px-2 text-2xs">Archiviert</span> }
  </div>
</ng-template>

<!-- Read-only: the type is derived by SolarInsight's detection task from the selected media. -->
<ng-template #typeTpl let-analysis>
  @if (analysis.analysisType) {
    <span class="inline-flex h-5 items-center rounded-sm border border-border px-1.5 font-mono text-2xs text-foreground">{{ analysis.analysisType | uppercase }}</span>
  } @else {
    <span class="text-muted-foreground" aria-label="Nicht gesetzt">—</span>
  }
</ng-template>

<ng-template #addressTpl let-analysis>
  <div class="flex items-center gap-2">
    <div class="min-w-0">
      @if (analysis.address) {
        <div class="text-sm truncate">
          {{ analysis.address.street }} {{ analysis.address.houseNumber }}
        </div>
        <div class="text-xs text-muted-foreground truncate">
          {{ analysis.address.postalCode }} {{ analysis.address.city }}
        </div>
      } @else {
        <div class="text-sm truncate">{{ formatAddress(analysis) }}</div>
      }
    </div>
  </div>
</ng-template>

<ng-template #consumptionTpl let-analysis>
  @if (analysis.powerConsumption != null || analysis.electricityPrice != null) {
    <div class="text-right tabular-nums">
      <div class="text-sm text-foreground">{{ analysis.powerConsumption != null ? (analysis.powerConsumption | number: "1.0-0") + ' kWh/a' : '—' }}</div>
      <div class="text-xs text-muted-foreground">{{ analysis.electricityPrice != null ? (analysis.electricityPrice | number: "1.2-4") + ' €/kWh' : '—' }}</div>
    </div>
  } @else {
    <div class="text-right text-muted-foreground">—</div>
  }
</ng-template>

<ng-template #statusTpl let-analysis>
  <app-analysis-status-selector [analysis]="analysis" [disabled]="!currentOrgStore.can('analysis', 'update')" (statusChanged)="onStatusChange(analysis, $event)" />
</ng-template>

<ng-template #dateTpl let-analysis>
  <span class="whitespace-nowrap font-mono text-2xs text-muted-foreground">{{ analysis.createdAt | date: "dd.MM.yyyy" }}</span>
</ng-template>

<ng-template #sourceTpl let-analysis>
  <app-analysis-source-badge [source]="analysis.source ?? (analysis.campaignId ? 'campaign' : 'hub')" />
</ng-template>

<!-- Execution state and pipeline stage in one badge: "Läuft · Analyse". -->
<ng-template #executionTpl let-analysis>
  <div class="flex flex-col gap-1">
    <span class="inline-flex items-center gap-1.5 whitespace-nowrap text-dense" [class]="isExecutionRunning(analysis) ? 'text-accent-foreground' : 'text-muted-foreground'">
      <ng-icon [name]="executionIndicator(executionState(analysis)).icon" class="size-3.5 shrink-0" [class]="executionIndicator(executionState(analysis)).class" />
      {{ executionLabel(analysis) }}
    </span>
    @if (executionState(analysis) === 'failed') {
      <app-analysis-error-detail
        [compact]="true"
        [errorCode]="analysis.technicalErrorCode ?? analysis.technicalStatus?.errorCode"
        [errorMessage]="analysis.technicalErrorMessage ?? analysis.technicalStatus?.errorMessage"
        [errorDetail]="analysis.technicalErrorDetail ?? analysis.technicalStatus?.errorDetail"
      />
    }
  </div>
</ng-template>

@if (campaignId()) {
  <app-download-dialog
    [organizationId]="organizationId()"
    [campaignId]="campaignId()!"
  />
}
